/**
 * CRS definitions, detection, and coordinate transformations.
 * 1:1 port of ui/src/viewer/crs.js from the desktop viewer.
 */
import proj4 from 'proj4'

// ── CRS Registry ──────────────────────────────────

export interface CrsDef {
  name: string
  proj4: string | null
  eastingLabel: string
  northingLabel: string
}

export const CRS_DEFS: Record<string, CrsDef> = {
  'EPSG:25832': {
    name: 'UTM Zone 32N (ETRS89)',
    proj4: '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
    eastingLabel: 'E',
    northingLabel: 'N',
  },
  'EPSG:25833': {
    name: 'UTM Zone 33N (ETRS89)',
    proj4: '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
    eastingLabel: 'E',
    northingLabel: 'N',
  },
  'EPSG:31467': {
    name: 'Gauß-Krüger Zone 3 (DHDN)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=9 +k=1 +x_0=3500000 +y_0=0 +ellps=bessel +towgs84=598.1,73.7,418.2,0.202,0.045,-2.455,6.7 +units=m +no_defs',
    eastingLabel: 'R',
    northingLabel: 'H',
  },
  'EPSG:31468': {
    name: 'Gauß-Krüger Zone 4 (DHDN)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=12 +k=1 +x_0=4500000 +y_0=0 +ellps=bessel +towgs84=598.1,73.7,418.2,0.202,0.045,-2.455,6.7 +units=m +no_defs',
    eastingLabel: 'R',
    northingLabel: 'H',
  },
  LOCAL: {
    name: 'Lokal (Offline)',
    proj4: null,
    eastingLabel: 'X',
    northingLabel: 'Y',
  },
}

for (const [code, def] of Object.entries(CRS_DEFS)) {
  if (def.proj4) proj4.defs(code, def.proj4)
}

// ── WGS84 ellipsoid constants ─────────────────────
const _WGS84_A = 6378137.0
const _WGS84_E2 = 0.00669437999014

export interface GeodeticDeg {
  lat: number
  lon: number
  alt: number
}

export function ecefToGeodeticDeg(x: number, y: number, z: number): GeodeticDeg {
  const lonRad = Math.atan2(y, x)
  const p = Math.sqrt(x * x + y * y)
  let latRad = Math.atan2(z, p * (1 - _WGS84_E2))
  for (let i = 0; i < 6; i++) {
    const sinLat = Math.sin(latRad)
    const N = _WGS84_A / Math.sqrt(1 - _WGS84_E2 * sinLat * sinLat)
    latRad = Math.atan2(z + _WGS84_E2 * N * sinLat, p)
  }
  const sinLat = Math.sin(latRad)
  const N = _WGS84_A / Math.sqrt(1 - _WGS84_E2 * sinLat * sinLat)
  const alt = p / Math.cos(latRad) - N
  return { lat: (latRad * 180) / Math.PI, lon: (lonRad * 180) / Math.PI, alt }
}

export function geodeticDegToEcef(
  latDeg: number,
  lonDeg: number,
  alt: number,
): { x: number; y: number; z: number } {
  const latRad = (latDeg * Math.PI) / 180
  const lonRad = (lonDeg * Math.PI) / 180
  const sinLat = Math.sin(latRad)
  const cosLat = Math.cos(latRad)
  const N = _WGS84_A / Math.sqrt(1 - _WGS84_E2 * sinLat * sinLat)
  return {
    x: (N + alt) * cosLat * Math.cos(lonRad),
    y: (N + alt) * cosLat * Math.sin(lonRad),
    z: (N * (1 - _WGS84_E2) + alt) * sinLat,
  }
}

// ── Height Systems ────────────────────────────────

export const HeightSystem = Object.freeze({
  ELLIPSOIDAL: 'ellipsoidal',
  DHHN2016: 'dhhn2016',
} as const)
export type HeightSystemValue = (typeof HeightSystem)[keyof typeof HeightSystem]

// ── CRS Detection ─────────────────────────────────

export function detectCRS(filename: string | null, fileContent: string | null): string {
  if (fileContent) {
    const lines = fileContent.split('\n')
    for (const line of lines) {
      const trimmed = line.trim()
      if (trimmed.startsWith('#')) {
        const match = trimmed.match(/EPSG[:\s]+(\d{4,5})/i)
        if (match) {
          const num = match[1]
          const mapping: Record<string, string> = {
            '32632': 'EPSG:25832',
            '32633': 'EPSG:25833',
            '25832': 'EPSG:25832',
            '25833': 'EPSG:25833',
            '31467': 'EPSG:31467',
            '31468': 'EPSG:31468',
          }
          if (mapping[num]) return mapping[num]
          const code = `EPSG:${num}`
          if (CRS_DEFS[code]) return code
        }
      }
    }
  }

  const lower = (filename || '').toLowerCase()
  if (lower.includes('utm32n') || lower.includes('utm32')) return 'EPSG:25832'
  if (lower.includes('utm33n') || lower.includes('utm33')) return 'EPSG:25833'
  if (lower.includes('gk3') || lower.includes('gk_3')) return 'EPSG:31467'
  if (lower.includes('gk4') || lower.includes('gk_4')) return 'EPSG:31468'

  return 'EPSG:25832'
}

// ── Coordinate Transformation ─────────────────────

export function transformCoords(
  easting: number,
  northing: number,
  sourceCRS: string,
  targetCRS: string,
): { easting: number; northing: number } {
  if (sourceCRS === targetCRS || sourceCRS === 'LOCAL' || targetCRS === 'LOCAL') {
    return { easting, northing }
  }
  const [x, y] = proj4(sourceCRS, targetCRS, [easting, northing])
  return { easting: x, northing: y }
}

export function applyHeightCorrection(
  ellipsoidalHeight: number,
  geoidUndulation: number,
  heightSystem: HeightSystemValue,
): number {
  if (heightSystem === HeightSystem.DHHN2016) {
    return ellipsoidalHeight - geoidUndulation
  }
  return ellipsoidalHeight
}

export function getAxisLabels(crsCode: string): { easting: string; northing: string } {
  const def = CRS_DEFS[crsCode]
  if (!def) return { easting: 'E', northing: 'N' }
  return { easting: def.eastingLabel, northing: def.northingLabel }
}
